import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from 'apps/cdp-web-demo/app/(main)/UserContext';
import { postProcessTree } from 'apps/cdp-web-demo/app/(main)/utils/generateCodeSnippet';
import Checkmark from 'apps/cdp-web-demo/app/icons/checkmark.svg';
import Copy from 'apps/cdp-web-demo/app/icons/copy.svg';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box } from '@cbhq/cds-web/layout';
import { SegmentedTabs, SegmentedTabsProps } from '@cbhq/cds-web/tabs/SegmentedTabs';
import { Text } from '@cbhq/cds-web/typography/Text';

import { CodeSnippet } from '../CodeSnippet';
import { DemoContent } from '../DemoContent/DemoContent';
import { SignIn } from '../SignIn/SignIn';

import styles from './Tabs.module.css';

type TabsProps = {
  code: string;
};

function Tabs({ code }: TabsProps) {
  const { isInitialized, isLoggedIn, isModalVisible, onSignInSuccess } = useUser();
  const showTabs = isInitialized && !isLoggedIn;

  const tabs = useMemo<SegmentedTabsProps['tabs']>(
    () => [
      { id: 'preview', label: 'Preview' },
      { id: 'code', label: 'Code' },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [isCopied, setIsCopied] = useState(false);

  const handleTabChange = useCallback((tab: (typeof tabs)[number] | null) => {
    if (!tab) {
      return;
    }
    setActiveTab(tab);
  }, []);

  const handleCopyCode = useCallback(() => {
    const doCopy = async () => {
      if (!code) return;
      try {
        // eslint-disable-next-line compat/compat
        await navigator.clipboard?.writeText(code);
        setIsCopied(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    };
    void doCopy();
  }, [code]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (isCopied) {
      timeout = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isCopied]);

  useEffect(() => {
    if (!showTabs) {
      setActiveTab((prevTab) => (prevTab.id === 'code' ? tabs[0] : prevTab));
    }
  }, [showTabs, activeTab, tabs]);

  return (
    <>
      {showTabs && (
        <Box
          padding={1}
          background="bg"
          borderRadius={1000}
          zIndex={2}
          className={styles['tabs-control']}
        >
          <SegmentedTabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </Box>
      )}
      <div className={styles['tabs-content']}>
        <div
          inert={activeTab?.id !== 'preview'}
          className={`${styles['preview-box']} ${activeTab?.id === 'preview' ? styles.active : ''} ${isInitialized ? styles.initialized : ''} ${isLoggedIn ? styles['logged-in'] : ''}`}
        >
          <DemoContent className={styles['demo-content']}>
            <SignIn isOpen={isModalVisible} onSuccess={onSignInSuccess} />
          </DemoContent>
        </div>
        <Box
          flexGrow={1}
          flexDirection="column"
          overflow="hidden"
          background="bgSecondaryWash"
          borderRadius={400}
          className={`${styles['code-box']} ${activeTab?.id === 'code' ? styles.active : ''}`}
          inert={activeTab?.id !== 'code'}
        >
          <div className={styles['code-box-header']}>
            <p>main.tsx</p>
            <button onClick={handleCopyCode} aria-label="Copy code" type="button">
              {isCopied ? (
                <Checkmark role="img" aria-hidden="true" className={styles.copy} />
              ) : (
                <Copy role="img" aria-hidden="true" className={styles.copy} />
              )}
            </button>
          </div>
          <CodeSnippet
            postProcess={postProcessTree}
            code={code}
            className={styles['code-snippet']}
          />
          <div className={styles['docs-banner']}>
            <Icon name="artwork" size="s" color="fg" />
            <Text font="label1">
              Get more advanced customization in{' '}
              <a
                href="https://docs.cdp.coinbase.com/wallets/client-side-development/react-components#3-customize-theme-optional"
                target="_blank"
                rel="noopener noreferrer"
              >
                our docs
              </a>
            </Text>
            <Icon name="forwardArrow" size="s" color="fgPrimary" />
          </div>
        </Box>
      </div>
    </>
  );
}

export { Tabs };
